import { type ReactNode, useEffect, useState } from "react"
import { Navigate } from "react-router-dom"
import {
  AuthenticatedAppRouteContext,
  type AppRouteAuthMembership,
  type AppRouteAuthOrganisation,
  type AppRouteAuthState,
  type AppRouteAuthUser,
} from "@/components/auth/authenticatedAppRouteContext"
import { Button } from "@/components/ui/button"
import { LoadingState } from "@/components/ui/loading-state"
import { apiFetch, getErrorMessage } from "@/lib/api"
import { syncPostHogIdentityFromAuthState } from "@/lib/posthog/identity"
import { setResourceCacheScope } from "@/lib/resourceCache"

type AuthenticatedAppRouteProps = {
  children: ReactNode
}

export function AuthenticatedAppRoute({ children }: AuthenticatedAppRouteProps) {
  const [authState, setAuthState] = useState<AppRouteAuthState | null>(null)
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    let cancelled = false

    const loadAuthState = async () => {
      try {
        const response = await apiFetch("/api/auth/me/")
        if (!response.ok) {
          throw new Error("Could not load session.")
        }

        const data = await response.json()
        syncPostHogIdentityFromAuthState(data)
        if (cancelled) {
          return
        }

        const nextAuthState = parseAppRouteAuthState(data)
        setResourceCacheScope(getAppRouteResourceCacheScope(nextAuthState))
        setAuthState(nextAuthState)
        setErrorMessage("")
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(getErrorMessage(error, "Could not load session."))
        }
      }
    }

    void loadAuthState()

    return () => {
      cancelled = true
    }
  }, [])

  if (!authState) {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background px-6 text-foreground">
        <div className="space-y-3 text-center">
          {errorMessage ? <p className="text-sm text-muted-foreground">{errorMessage}</p> : null}
          {!errorMessage ? <LoadingState message="Loading session…" /> : null}
          {errorMessage ? (
            <Button type="button" variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
          ) : null}
        </div>
      </main>
    )
  }

  if (!authState.authenticated) {
    return <Navigate to="/" replace />
  }

  if (!authState.activeOrganisation || authState.requiresOrganisationSetup) {
    return <Navigate to="/app" replace />
  }

  return (
    <AuthenticatedAppRouteContext.Provider value={authState}>
      {children}
    </AuthenticatedAppRouteContext.Provider>
  )
}

function parseAppRouteAuthState(data: unknown): AppRouteAuthState {
  const responseData = data && typeof data === "object"
    ? data as {
      activeOrganisation?: unknown
      authenticated?: unknown
      memberships?: unknown
      requiresOrganisationSetup?: unknown
      user?: unknown
    }
    : {}

  return {
    activeOrganisation: isAppRouteAuthOrganisation(responseData.activeOrganisation)
      ? responseData.activeOrganisation
      : null,
    authenticated: Boolean(responseData.authenticated),
    memberships: Array.isArray(responseData.memberships)
      ? responseData.memberships.filter(isAppRouteAuthMembership)
      : [],
    requiresOrganisationSetup: Boolean(responseData.requiresOrganisationSetup),
    user: isAppRouteAuthUser(responseData.user) ? responseData.user : null,
  }
}

function getAppRouteResourceCacheScope(authState: AppRouteAuthState) {
  if (!authState.authenticated || !authState.activeOrganisation) {
    return "app-route:anonymous"
  }

  return `app-route:user:${authState.user?.id ?? "unknown"}:organisation:${authState.activeOrganisation.id}`
}

function isAppRouteAuthUser(value: unknown): value is AppRouteAuthUser {
  if (!value || typeof value !== "object") {
    return false
  }

  const user = value as { email?: unknown; firstName?: unknown; id?: unknown; lastName?: unknown }
  return (
    typeof user.id === "number" &&
    typeof user.email === "string" &&
    typeof user.firstName === "string" &&
    typeof user.lastName === "string"
  )
}

function isAppRouteAuthOrganisation(value: unknown): value is AppRouteAuthOrganisation {
  if (!value || typeof value !== "object") {
    return false
  }

  return typeof (value as { id?: unknown }).id === "number"
}

function isAppRouteAuthMembership(value: unknown): value is AppRouteAuthMembership {
  if (!value || typeof value !== "object") {
    return false
  }

  return typeof (value as { organisation?: { id?: unknown } }).organisation?.id === "number"
}
